// src/hooks/useMpAttemptChannel.ts
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSocketContext } from '../app/context/socketContext';
import { IOrderPaymentLines } from '../interfaces/Order';

export type AttemptStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'refunded'
  | 'charged_back';

export type MpAttemptUpdate = {
  attemptId: string;
  status: AttemptStatus;
  mp_order_id?: string | null;
  mp_payment_id?: string | null;
  external_reference?: string | null;
  orderStatus?: string;
  paymentStatus?: string;
  ts?: number;
  order?: {
    _id: string;
    sequence: string;
    paymentLines?: IOrderPaymentLines[];
  };
  amount?: number;
};

type Options = {
  attemptId: string;
  enabled: boolean;
  onEvent?: (evt: MpAttemptUpdate) => void;
  hydrate?: () => Promise<MpAttemptUpdate | null | undefined>;
};

export function useMpAttemptChannel({ attemptId, onEvent, hydrate, enabled }: Options) {
  const { socket, online } = useSocketContext();
  const [last, setLast] = useState<MpAttemptUpdate | null>(null);

  // Refs para dedupe sin re-suscribir listeners
  const lastTsRef = useRef<number>(0);
  const lastRef = useRef<MpAttemptUpdate | null>(null);

  // Hidratar por API si el caller lo pide (opcional)
  useEffect(() => {
    let cancelled = false;
    if (hydrate && enabled) {
      hydrate()
        .then((snap) => {
          if (!cancelled && snap && snap.attemptId === attemptId) {
            const ts = typeof snap.ts === 'number' ? snap.ts : Date.now();
            lastTsRef.current = ts;
            lastRef.current = snap;
            setLast(snap);
          }
        })
        .catch(() => void 0);
    }
    return () => {
      cancelled = true;
    };
  }, [attemptId, hydrate, enabled]);

  // Suscripción al room del intento + snapshot por ACK
  useEffect(() => {
    if (!socket || !enabled || !attemptId) return;
    let mounted = true;

    socket.emit(
      'mpAttempt:sub',
      { attemptId },
      (resp?: { ok: boolean; snapshot?: MpAttemptUpdate; error?: string }) => {
        if (!mounted) return;
        if (resp?.ok && resp.snapshot && resp.snapshot.attemptId === attemptId) {
          const ts = typeof resp.snapshot.ts === 'number' ? resp.snapshot.ts : Date.now();
          lastTsRef.current = ts;
          lastRef.current = resp.snapshot;
          setLast(resp.snapshot);
        }
      }
    );

    return () => {
      mounted = false;
      socket.emit('mpAttempt:unsub', { attemptId });
    };
  }, [socket, enabled, attemptId]);

  // Listener del evento de updates (unificado)
  useEffect(() => {
    if (!socket || !enabled || !attemptId) return;

    const handler = (payload: MpAttemptUpdate) => {
      if (!payload || payload.attemptId !== attemptId) return;

      const ts = typeof payload.ts === 'number' ? payload.ts : Date.now();
      const prev = lastRef.current;

      const sameAsLast =
        prev &&
        payload.status === prev.status &&
        (payload.mp_payment_id || null) === (prev.mp_payment_id || null);

      // dedupe (no retroceder en el tiempo y evitar duplicados exactos)
      if (ts <= lastTsRef.current && sameAsLast) return;

      lastTsRef.current = ts;
      lastRef.current = payload;
      setLast(payload);
      onEvent?.(payload);
    };

    socket.on('mpAttempt:update', handler);
    return () => {
      socket.off('mpAttempt:update', handler);
    };
  }, [socket, enabled, attemptId, onEvent]);

  // Flags para la UI
  const flags = useMemo(
    () => ({
      isPending: last?.status === 'pending',
      isApproved: last?.status === 'approved',
      isRejected: last?.status === 'rejected',
      isCancelled: last?.status === 'cancelled',
      isRefunded: last?.status === 'refunded',
      isChargedBack: last?.status === 'charged_back'
    }),
    [last?.status]
  );

  return {
    online,
    last,
    ...flags
  };
}
